// Central display-date formatting so every date the user sees reads
// '18 August 2026' (not DD-MM-YYYY).
//
// Design contract (see CLAUDE.md / docs): dates travel on the wire and live in
// <input type="date"> as ISO 'YYYY-MM-DD'. Those MUST stay ISO. These helpers
// only touch what is rendered to the user — never a value sent back to the API,
// used as a sort key, or bound to a date input.

const pad = (n) => String(n).padStart(2, '0');

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// month is 1-based; out-of-range input falls back to the number so a bad value
// is visible rather than silently blank.
const monthName = (m) => MONTHS[m - 1] || String(m);

// 'Aug'. Timestamps have no room for the full word but must not fall back to a
// number: '20-08-2026' invites the reader to parse it as month-first, and half
// the year every such date is also a valid US-order date.
const monthAbbr = (m) => (MONTHS[m - 1] ? MONTHS[m - 1].slice(0, 3) : String(m));

const spell = (d, m, y) => `${pad(d)} ${monthName(m)} ${y}`;

/**
 * Format any date-ish value for display as 'DD Month YYYY' (e.g. 18 August 2026).
 * Accepts ISO date/datetime ('YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss'), day-first
 * strings ('DD/MM/YYYY' or 'DD-MM-YYYY'), and Date objects. Returns '' for
 * empty values and the original string for anything unparseable.
 * String shapes are read positionally (no Date round-trip) so there is no
 * timezone shift of the calendar day.
 */
export function formatDate(value) {
  if (value === null || value === undefined || value === '') return '';
  if (value instanceof Date) {
    return isNaN(value.getTime())
      ? ''
      : spell(value.getDate(), value.getMonth() + 1, value.getFullYear());
  }
  const s = String(value).trim();
  // ISO date or datetime: 'YYYY-MM-DD' possibly followed by 'T…'
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return spell(Number(m[3]), Number(m[2]), m[1]);
  // Already day-first with '/' or '-': 'DD/MM/YYYY' / 'DD-MM-YYYY'
  m = s.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
  if (m) return spell(Number(m[1]), Number(m[2]), m[3]);
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : spell(d.getDate(), d.getMonth() + 1, d.getFullYear());
}

/**
 * Format any date-ish value for display as 'DD Mon YYYY' (e.g. 30 Sep 2026) —
 * the table form of formatDate. Every table date column uses it: the short
 * month keeps each column one line wide beside long names, and stays a word
 * rather than an ambiguous number.
 * Also used wherever a date sits inside a compact line: dropdown option labels,
 * the grey meta line under a name ("Group · FYE 31 Dec 2025"), chips and the
 * coverage picker rows. Headings, labelled detail fields and full sentences
 * (confirm dialogs, messages) spell the month in full with formatDate.
 */
export function formatDateShort(value) {
  const s = formatDate(value);
  const m = s.match(/^(\d{2}) ([A-Za-z]+) (\d{4})$/);
  return m ? `${m[1]} ${m[2].slice(0, 3)} ${m[3]}` : s;
}

/**
 * Format any date-ish value for display as 'DD-Mon-YYYY HH:mm' (24h, local
 * time), e.g. '20-Aug-2026 13:36'.
 * Returns '' for empty values and the original string for anything unparseable.
 *
 * The month is spelled short rather than numeric — the
 * calendar date keeps the full month name (formatDate), and a timestamp that
 * reads '20-08-2026' is ambiguous to anyone who reads dates month-first.
 */
export function formatDateTime(value) {
  if (value === null || value === undefined || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return `${pad(d.getDate())}-${monthAbbr(d.getMonth() + 1)}-${d.getFullYear()} `
    + `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
